/**
 * E1 · Aplicações RDB (0055) — a aplicação é ligada à conta corrente; tudo por inclusão, com prévia sem gravação.
 *  - Lista: principal calculado por aplicação, posição provisória identificada, pendências (composição a informar,
 *    posição divergente, aguardando extrato).
 *  - Ficha: eventos (posição inicial PIA sem saída bancária, aplicação APL, resgate RES pelo líquido, posição informada
 *    POS), composição do resgate em 5 campos com soma = líquido (versões anteriores no histórico), realizado por mês
 *    (rendimento, IR, IOF e tarifa só do que foi informado), anotações (anexo, observação, ciência justificada da
 *    divergência — que não a dá por resolvida: a pendência continua).
 *  - Mês fechado: incluir ou alterar composição, evento ou estorno exige reabrir o período; sem reabrir, só anexo ou
 *    observação.
 *  - Conciliação com o extrato (ConciliarAplicacao, aberta na Conciliação): movimentos de aplicação/resgate × APL/RES
 *    aguardando extrato; sugestão nunca marcada; soma exata; ou registrar o evento a partir do movimento e conciliar.
 */
import { useEffect, useMemo, useState } from 'react';
import { api } from '../api.ts';
import { navegar } from '../rotas.ts';
import { useSessao } from '../sessao.tsx';
import { Ajuda, CardLista, CardsLista, Codigo, EstadoCarga, Modal, RolagemIndicada, SeloSituacao, useCarga, type Tom } from '../comercial/ui.tsx';
import { arquivoParaApi } from '../comercial/Pedidos.tsx';
import { dataBr, mesExtenso, plural, rs } from './ContasPagar.tsx';
import { Campo } from '../componentes/basicos.tsx';

const msg = (e: unknown) => (e as Error).message;
/** centavos de "1.234,56" (digitado) ou "1234.56" (da API) */
const centavos = (v: string | number) => Math.round(Number(String(v).includes(',') ? String(v).replace(/\./g, '').replace(',', '.') : v || 0) * 100);
const valorConta = (v: string) => (
  <span className={Number(v) < 0 ? 'conc-saida' : 'conc-entrada'}>
    {Number(v) > 0 ? '+' : ''}
    {rs(v)}
  </span>
);

type TipoEvento = 'pia' | 'apl' | 'res' | 'pos';
interface Composicao { principal: string; rendimentoBruto: string; ir: string; iof: string; tarifa: string; origem: string; documento: string; justificativa: string | null; em: string; por: string | null }
interface Evento {
  id: string; numero: string; tipo: TipoEvento; data: string; valor: string; posicaoBruta: string | null; dataAplicacaoOriginal: string | null; encerra: boolean; origem: string; documento: string; observacao: string | null; em: string; por: string | null;
  estorno: { justificativa: string; em: string; por: string | null } | null;
  composicao: Composicao | null; composicoesAnteriores: Composicao[]; conciliacao: string | null;
  anotacoes: { tipo: 'observacao' | 'anexo' | 'aceite_divergencia'; texto: string; arquivoId: string | null; em: string; por: string | null }[];
}
interface Pendencia { tipo: 'composicao_a_informar' | 'posicao_divergente' | 'aguardando_extrato'; evento: string; data: string; valor: string; texto: string }
interface Posicao {
  id: string; numero: string; descricao: string; produto: string; conta: { id: string; banco: string; agencia: string; numero: string }; ate: string;
  principal: string; provisoria: boolean; faltaDeduzir: { evento: string; liquido: string }[]; encerrada: boolean;
  ultimaInformada: { evento: string; data: string; principal: string; posicaoBruta: string | null; calculadoNaData: string; diferenca: string; ciencia: { texto: string; em: string; por: string | null } | null } | null;
  pendencias: Pendencia[];
}
interface Permissoes { registrar: boolean; corrigir: boolean; estornar: boolean; conciliar: boolean; desfazer: boolean }
interface Lista { hoje: string; contas: { id: string; banco: string; agencia: string; numero: string }[]; aplicacoes: Posicao[]; totalPrincipal: string; totalProvisorio: boolean; permissoes: Permissoes }
interface RealizadoMes { mes: string; aplicado: string; resgatadoLiquido: string; principalResgatado: string; rendimentoBruto: string; ir: string; iof: string; tarifa: string; resgatesAInformar: number }
interface Ficha extends Posicao { eventos: Evento[]; porMes: RealizadoMes[]; mesesFechados: string[]; permissoes: Permissoes }
interface Efeito { principalAntes: string; principalDepois: string; provisoriaAntes: boolean; provisoriaDepois: boolean; pendenciasAntes: string[]; pendenciasDepois: string[] }

const TIPO: Record<TipoEvento, { sigla: string; rotulo: string }> = {
  pia: { sigla: 'PIA', rotulo: 'Posição inicial' },
  apl: { sigla: 'APL', rotulo: 'Aplicação' },
  res: { sigla: 'RES', rotulo: 'Resgate' },
  pos: { sigla: 'POS', rotulo: 'Posição informada' },
};
const ORIGENS: [string, string][] = [['comprovante', 'Comprovante'], ['demonstrativo', 'Demonstrativo do banco'], ['extrato', 'Extrato'], ['informado', 'Informado'], ['outro', 'Outro']];
const TOM_PEND: Record<Pendencia['tipo'], Tom> = { composicao_a_informar: 'parcial', posicao_divergente: 'alerta', aguardando_extrato: 'aberto' };
const ROTULO_PEND: Record<Pendencia['tipo'], string> = { composicao_a_informar: 'Composição a informar', posicao_divergente: 'Posição divergente', aguardando_extrato: 'Aguardando extrato' };
const contaTexto = (c: { banco: string; agencia: string; numero: string }) => `${c.banco} · ag. ${c.agencia} · conta ${c.numero}`;
const situacao = (p: Posicao): { texto: string; tom: Tom } => (p.encerrada ? { texto: 'Encerrada', tom: 'encerrado' } : p.provisoria ? { texto: 'Posição provisória', tom: 'parcial' } : { texto: 'Ativa', tom: 'positivo' });
const Erro = ({ texto, teste }: { texto: string; teste: string }) =>
  texto ? (
    <p className="aviso aviso-erro" role="alert" data-teste={teste}>
      {texto}
    </p>
  ) : null;

/** Efeito da prévia: antes → depois, com as pendências que surgem e as que se resolvem. Nada foi gravado. */
function EfeitoPrevia({ efeito, teste }: { efeito: Efeito; teste: string }) {
  const novas = efeito.pendenciasDepois.filter((p) => !efeito.pendenciasAntes.includes(p));
  const resolvidas = efeito.pendenciasAntes.filter((p) => !efeito.pendenciasDepois.includes(p));
  return (
    <div className="faixa-resumo-modal conc-bloco" data-teste={teste}>
      <div>
        <strong>Prévia (nada gravado):</strong> principal {rs(efeito.principalAntes)} → <strong>{rs(efeito.principalDepois)}</strong>
        {efeito.provisoriaDepois ? ' · posição provisória' : efeito.provisoriaAntes ? ' · deixa de ser provisória' : ''}
      </div>
      {novas.map((p) => (
        <div key={p} className="texto-secundario">
          + {p}
        </div>
      ))}
      {resolvidas.map((p) => (
        <div key={p} className="texto-secundario">
          ✓ resolvida: {p}
        </div>
      ))}
    </div>
  );
}

/* ---------------------------------------------------------------- lista */
export function TelaAplicacoes() {
  const carga = useCarga(() => api.get<Lista>('/api/aplicacoes'), []);
  const [nova, setNova] = useState(false);
  const d = carga.dados;
  if (!d) return <EstadoCarga carregando={carga.carregando} erro={carga.erro} recarregar={carga.recarregar} teste="aplicacoes" />;
  const ativas = d.aplicacoes.filter((a) => !a.encerrada);
  const pend = d.aplicacoes.flatMap((a) => a.pendencias);
  const conta = (t: Pendencia['tipo']) => pend.filter((p) => p.tipo === t).length;
  return (
    <>
      <h1 className="titulo-pagina rotulo-oculto">Aplicações</h1>
      <CardsLista n={3} teste="cards-aplicacoes">
        <CardLista
          icone="dinheiro"
          tom="azul"
          rotulo="Posição investida (principal)"
          valor={rs(d.totalPrincipal)}
          detalhe={`${plural(ativas.length, 'aplicação ativa', 'aplicações ativas')} · em ${dataBr(d.hoje)}${d.totalProvisorio ? ' · provisória' : ''}`}
          ajuda={
            <Ajuda titulo="Posição investida">
              Principal calculado: posição inicial + aplicações − principal dos resgates com composição informada. Resgate com composição “a informar” não é deduzido pelo líquido: a posição fica provisória até a composição chegar. Não se soma ao caixa disponível da conta corrente.
            </Ajuda>
          }
          teste="card-posicao-investida"
        />
        <CardLista
          icone="alerta"
          tom="roxo"
          rotulo="Pendências"
          valor={plural(pend.length, 'pendência', 'pendências')}
          detalhe={`${conta('composicao_a_informar')} composição a informar · ${conta('posicao_divergente')} posição divergente · ${conta('aguardando_extrato')} aguardando extrato`}
          ajuda={<Ajuda titulo="Pendências das aplicações">Composição a informar: resgate pelo líquido sem a separação entre principal, rendimento, IR, IOF e tarifa. Posição divergente: demonstrativo do banco diferente do calculado (nada é ajustado sozinho). Aguardando extrato: aplicação ou resgate registrado e ainda não conciliado com o movimento da conta.</Ajuda>}
          teste="card-pendencias-aplicacoes"
        />
        <CardLista
          icone="documento"
          tom="verde"
          rotulo="Contas correntes"
          valor={plural(new Set(d.aplicacoes.map((a) => a.conta.id)).size, 'conta', 'contas')}
          detalhe="cada aplicação é ligada à conta de onde o dinheiro sai e para onde volta"
          teste="card-contas-aplicacoes"
        />
      </CardsLista>
      <div className="barra-filtros conc-barra">
        <span className="conc-espaco" />
        <button type="button" className="botao-secundario" onClick={() => navegar('/conciliacao')} data-teste="ir-conciliacao">
          Conciliação bancária
        </button>
        {d.permissoes.registrar && (
          <button type="button" className="botao-primario" onClick={() => setNova(true)} data-teste="nova-aplicacao">
            Nova aplicação
          </button>
        )}
      </div>
      {d.aplicacoes.length ? (
        <RolagemIndicada className="rolagem quadro-tabela">
          <table className="tabela tabela-compacta" data-teste="tabela-aplicacoes">
            <thead>
              <tr>
                <th>Aplicação</th>
                <th>Conta corrente</th>
                <th className="numero">Principal calculado</th>
                <th>Situação</th>
                <th>Pendências</th>
              </tr>
            </thead>
            <tbody>
              {d.aplicacoes.map((a) => (
                <tr key={a.numero} data-teste={`aplicacao-${a.numero}`}>
                  <td>
                    <a href={`#/aplicacoes/${a.numero}`} data-teste={`abrir-${a.numero}`}>
                      <strong>{a.descricao}</strong>
                    </a>
                    <div className="texto-secundario conc-mini">
                      <Codigo>{a.numero}</Codigo> · {a.produto.toUpperCase()}
                    </div>
                  </td>
                  <td>{contaTexto(a.conta)}</td>
                  <td className="numero conc-nowrap">
                    {rs(a.principal)}
                    {a.provisoria && <div className="texto-secundario conc-mini">falta deduzir {plural(a.faltaDeduzir.length, 'resgate', 'resgates')}</div>}
                  </td>
                  <td>
                    <SeloSituacao {...situacao(a)} />
                  </td>
                  <td>
                    {a.pendencias.length ? (
                      <span className="conc-acoes-pilha">
                        {(['composicao_a_informar', 'posicao_divergente', 'aguardando_extrato'] as const)
                          .filter((t) => a.pendencias.some((p) => p.tipo === t))
                          .map((t) => (
                            <span key={t} className="texto-secundario conc-mini">
                              {ROTULO_PEND[t]} ({a.pendencias.filter((p) => p.tipo === t).length})
                            </span>
                          ))}
                      </span>
                    ) : (
                      <span className="texto-secundario">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </RolagemIndicada>
      ) : (
        <p className="texto-secundario" data-teste="sem-aplicacoes">
          Nenhuma aplicação cadastrada. {d.permissoes.registrar ? 'Cadastre a aplicação e registre a posição inicial (na data do saldo inicial da conta) ou a primeira aplicação.' : ''}
        </p>
      )}
      {nova && <NovaAplicacao contas={d.contas} aoFechar={() => setNova(false)} aoCriar={(n) => navegar(`/aplicacoes/${n}`)} />}
    </>
  );
}

function NovaAplicacao({ contas, aoFechar, aoCriar }: { contas: Lista['contas']; aoFechar: () => void; aoCriar: (numero: string) => void }) {
  const [conta, setConta] = useState(contas[0]?.id ?? '');
  const [descricao, setDescricao] = useState('');
  const [erro, setErro] = useState('');
  const [enviando, setEnviando] = useState(false);
  const enviar = async () => {
    setEnviando(true);
    setErro('');
    try {
      const r = await api.post<{ numero: string }>('/api/aplicacoes', { conta, descricao });
      aoCriar(r.numero);
    } catch (e) {
      setErro(msg(e));
    } finally {
      setEnviando(false);
    }
  };
  return (
    <Modal
      titulo="Nova aplicação"
      aoFechar={aoFechar}
      medio
      rodape={
        <>
          <button type="button" className="botao-secundario" onClick={aoFechar}>
            Voltar
          </button>
          <button type="button" className="botao-primario" disabled={!conta || descricao.trim().length < 3 || enviando} onClick={() => void enviar()} data-teste="confirmar-nova-aplicacao">
            Cadastrar
          </button>
        </>
      }
    >
      <div data-teste="modal-nova-aplicacao">
        <div className="grade-form">
          <Campo rotulo="Conta corrente *" dica="De onde o dinheiro sai na aplicação e para onde volta no resgate.">
            {(id) => (
              <select id={id} value={conta} onChange={(e) => setConta(e.target.value)} data-teste="conta-aplicacao">
                {contas.map((c) => (
                  <option key={c.id} value={c.id}>
                    {contaTexto(c)}
                  </option>
                ))}
              </select>
            )}
          </Campo>
          <Campo rotulo="Descrição *">{(id) => <input id={id} maxLength={120} value={descricao} onChange={(e) => setDescricao(e.target.value)} placeholder="Ex.: RDB liquidez diária" data-teste="descricao-aplicacao" />}</Campo>
          <Campo rotulo="Produto">{(id) => <input id={id} value="RDB" readOnly />}</Campo>
        </div>
        <p className="texto-secundario">Cadastrar não movimenta a conta. Depois, registre a posição inicial (se a aplicação já existia na data do saldo inicial da conta) ou a primeira aplicação.</p>
        <Erro texto={erro} teste="erro-nova-aplicacao" />
      </div>
    </Modal>
  );
}

/* ---------------------------------------------------------------- ficha */
export function FichaAplicacao({ numero }: { numero: string }) {
  const carga = useCarga(() => api.get<Ficha>(`/api/aplicacoes/${encodeURIComponent(numero)}`), [numero]);
  const [registrar, setRegistrar] = useState<TipoEvento | null>(null);
  const [comp, setComp] = useState<Evento | null>(null);
  const [anotar, setAnotar] = useState<Evento | null>(null);
  const [estornar, setEstornar] = useState<Evento | null>(null);
  const [verHistorico, setVerHistorico] = useState<string | null>(null);
  const f = carga.dados;
  if (!f) return <EstadoCarga carregando={carga.carregando} erro={carga.erro} recarregar={carga.recarregar} teste="ficha-aplicacao" />;
  const recarregar = () => (setRegistrar(null), setComp(null), setAnotar(null), setEstornar(null), void carga.recarregar());
  const fechado = (data: string) => f.mesesFechados.includes(data.slice(0, 7));
  const temPia = f.eventos.some((e) => e.tipo === 'pia' && !e.estorno);
  const divergente = f.ultimaInformada && Number(f.ultimaInformada.diferenca) !== 0;
  return (
    <>
      <h1 className="titulo-pagina">
        {f.descricao} <Codigo>{f.numero}</Codigo> <SeloSituacao {...situacao(f)} />
      </h1>
      <p className="texto-secundario" data-teste="conta-ficha-aplicacao">
        {f.produto.toUpperCase()} · ligada à conta {contaTexto(f.conta)}
      </p>
      <CardsLista n={3} teste="cards-ficha-aplicacao">
        <CardLista
          icone="dinheiro"
          tom="azul"
          rotulo="Principal calculado"
          valor={rs(f.principal)}
          detalhe={f.provisoria ? `provisório: falta deduzir ${f.faltaDeduzir.map((x) => `${x.evento} (líquido ${rs(x.liquido)})`).join(', ')}` : `em ${dataBr(f.ate)}`}
          ajuda={<Ajuda titulo="Principal calculado">Posição inicial + aplicações − principal dos resgates com composição informada. O líquido de um resgate nunca é deduzido como se fosse principal: sem a composição, a posição fica provisória.</Ajuda>}
          teste="card-principal"
        />
        <CardLista
          icone="documento"
          tom="roxo"
          rotulo="Última posição informada"
          valor={f.ultimaInformada ? rs(f.ultimaInformada.principal) : '—'}
          detalhe={
            f.ultimaInformada
              ? `${f.ultimaInformada.evento} em ${dataBr(f.ultimaInformada.data)} · calculado na data ${rs(f.ultimaInformada.calculadoNaData)}${divergente ? ` · diferença ${rs(f.ultimaInformada.diferenca)} (divergente${f.ultimaInformada.ciencia ? ' · ciência registrada' : ''})` : ' · confere ✓'}${f.ultimaInformada.posicaoBruta ? ` · bruta ${rs(f.ultimaInformada.posicaoBruta)}: rendimento acumulado informado ${rs((Math.round(Number(f.ultimaInformada.posicaoBruta) * 100) - Math.round(Number(f.ultimaInformada.principal) * 100)) / 100)} (informativo, não somado ao realizado)` : ''}`
              : 'nenhum demonstrativo registrado'
          }
          ajuda={<Ajuda titulo="Posição informada">Principal do demonstrativo do banco, comparado com o calculado na mesma data. Divergência vira pendência, sem ajuste automático. Registrar ciência (com justificativa) não a resolve: ela continua até um demonstrativo que confira ou a correção dos eventos. A posição bruta (com rendimento acumulado) é só informativa.</Ajuda>}
          teste="card-ultima-posicao"
        />
        <CardLista
          icone="alerta"
          tom="verde"
          rotulo="Pendências"
          valor={plural(f.pendencias.length, 'pendência', 'pendências')}
          detalhe={f.pendencias.length ? f.pendencias.map((p) => ROTULO_PEND[p.tipo]).filter((x, i, a) => a.indexOf(x) === i).join(' · ') : 'nenhuma'}
          teste="card-pendencias-ficha"
        />
      </CardsLista>
      {f.pendencias.length > 0 && (
        <ul className="conc-lista" data-teste="pendencias-aplicacao">
          {f.pendencias.map((p) => (
            <li key={`${p.tipo}-${p.evento}`} data-teste={`pendencia-${p.tipo}-${p.evento}`}>
              <SeloSituacao texto={ROTULO_PEND[p.tipo]} tom={TOM_PEND[p.tipo]} /> {p.texto.startsWith(`${ROTULO_PEND[p.tipo]}: `) ? p.texto.slice(ROTULO_PEND[p.tipo].length + 2) : p.texto}
            </li>
          ))}
        </ul>
      )}
      <div className="barra-filtros conc-barra">
        <h2 className="conc-titulo">Eventos</h2>
        <span className="conc-espaco" />
        <button type="button" className="botao-secundario" onClick={() => navegar('/aplicacoes')}>
          Todas as aplicações
        </button>
        {f.permissoes.registrar && !f.encerrada && (
          <>
            {!temPia && (
              <button type="button" className="botao-secundario" onClick={() => setRegistrar('pia')} data-teste="registrar-pia">
                Posição inicial
              </button>
            )}
            <button type="button" className="botao-secundario" onClick={() => setRegistrar('pos')} data-teste="registrar-pos">
              Posição informada
            </button>
            <button type="button" className="botao-secundario" onClick={() => setRegistrar('res')} data-teste="registrar-res">
              Resgate
            </button>
            <button type="button" className="botao-primario" onClick={() => setRegistrar('apl')} data-teste="registrar-apl">
              Aplicação
            </button>
          </>
        )}
      </div>
      {f.eventos.length ? (
        <RolagemIndicada className="rolagem quadro-tabela">
          <table className="tabela tabela-compacta" data-teste="tabela-eventos">
            <thead>
              <tr>
                <th>Data</th>
                <th>Evento</th>
                <th className="numero">Valor</th>
                <th>Composição · extrato</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {f.eventos.map((e) => {
                const mesFechado = fechado(e.data);
                const movimenta = e.tipo === 'apl' || e.tipo === 'res';
                return (
                  <tr key={e.id} className={e.estorno ? 'conc-cancelado' : undefined} data-teste={`evento-${e.numero}`}>
                    <td className="conc-nowrap">
                      {dataBr(e.data)}
                      {mesFechado && <div className="texto-secundario conc-mini">mês fechado</div>}
                    </td>
                    <td>
                      <strong>{TIPO[e.tipo].rotulo}</strong> <Codigo>{e.numero}</Codigo>
                      {e.encerra && <> · resgate total (encerra)</>}
                      <div className="texto-secundario conc-mini">
                        {ORIGENS.find(([k]) => k === e.origem)?.[1] ?? e.origem}: {e.documento}
                        {e.dataAplicacaoOriginal && ` · aplicada originalmente em ${dataBr(e.dataAplicacaoOriginal)}`}
                        {e.posicaoBruta && ` · posição bruta ${rs(e.posicaoBruta)}`}
                        {e.observacao && ` · ${e.observacao}`}
                      </div>
                      {e.estorno && (
                        <div className="texto-secundario conc-mini" data-teste={`estorno-${e.numero}`}>
                          Estornado em {new Date(e.estorno.em).toLocaleString('pt-BR')}
                          {e.estorno.por ? ` por ${e.estorno.por}` : ''} — “{e.estorno.justificativa}”
                        </div>
                      )}
                      {e.anotacoes.map((a, i) => (
                        <div key={i} className="texto-secundario conc-mini">
                          {a.tipo === 'aceite_divergencia' ? 'Ciência da divergência' : a.tipo === 'anexo' ? 'Anexo' : 'Observação'}: {a.texto}
                          {a.arquivoId && (
                            <>
                              {' '}
                              <button type="button" className="botao-link" onClick={() => void baixarAnexo(a.arquivoId!)}>
                                baixar
                              </button>
                            </>
                          )}{' '}
                          · {new Date(a.em).toLocaleString('pt-BR')}
                          {a.por ? ` · ${a.por}` : ''}
                        </div>
                      ))}
                    </td>
                    <td className="numero conc-nowrap">{movimenta ? valorConta(e.tipo === 'apl' ? `-${e.valor}` : e.valor) : rs(e.valor)}</td>
                    <td>
                      {e.tipo !== 'res' ? (
                        <span className="texto-secundario">{e.tipo === 'pia' ? 'sem saída bancária' : e.tipo === 'pos' ? 'demonstrativo' : ''}</span>
                      ) : e.composicao ? (
                        <div className="conc-mini" data-teste={`composicao-${e.numero}`}>
                          principal {rs(e.composicao.principal)} · rend. bruto {rs(e.composicao.rendimentoBruto)} · IR {rs(e.composicao.ir)} · IOF {rs(e.composicao.iof)} · tarifa {rs(e.composicao.tarifa)}
                          {e.composicoesAnteriores.length > 0 && (
                            <div>
                              <button type="button" className="botao-link" onClick={() => setVerHistorico(verHistorico === e.numero ? null : e.numero)} data-teste={`versoes-${e.numero}`}>
                                {plural(e.composicoesAnteriores.length, 'versão anterior', 'versões anteriores')}
                              </button>
                            </div>
                          )}
                          {verHistorico === e.numero &&
                            e.composicoesAnteriores.map((c, i) => (
                              <div key={i} className="texto-secundario">
                                {new Date(c.em).toLocaleString('pt-BR')}
                                {c.por ? ` · ${c.por}` : ''}: principal {rs(c.principal)} · rend. {rs(c.rendimentoBruto)} · IR {rs(c.ir)} · IOF {rs(c.iof)} · tarifa {rs(c.tarifa)}
                              </div>
                            ))}
                          {e.composicao.justificativa && <div className="texto-secundario">Correção: “{e.composicao.justificativa}”</div>}
                        </div>
                      ) : e.estorno ? (
                        '—'
                      ) : (
                        <SeloSituacao texto="Composição a informar" tom="parcial" />
                      )}
                      {movimenta && !e.estorno && (
                        <div className="apl-extrato">{e.conciliacao ? <SeloSituacao texto={`Conciliado · ${e.conciliacao}`} tom="positivo" /> : <SeloSituacao texto="Aguardando extrato" tom="aberto" />}</div>
                      )}
                    </td>
                    <td className="conc-acoes">
                      {!e.estorno && (
                        <span className="conc-acoes-pilha apl-acoes">
                          {e.tipo === 'res' && f.permissoes.corrigir && (
                            <button type="button" className="botao-link" onClick={() => setComp(e)} data-teste={`composicao-acao-${e.numero}`}>
                              {e.composicao ? 'Corrigir composição' : 'Informar composição'}
                            </button>
                          )}
                          {f.permissoes.corrigir && (
                            <button type="button" className="botao-link" onClick={() => setAnotar(e)} title="Anexo, observação ou ciência da divergência" data-teste={`anotar-${e.numero}`}>
                              Anotar
                            </button>
                          )}
                          {f.permissoes.estornar && (
                            <button type="button" className="botao-link" onClick={() => setEstornar(e)} data-teste={`estornar-${e.numero}`}>
                              Estornar
                            </button>
                          )}
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </RolagemIndicada>
      ) : (
        <p className="texto-secundario" data-teste="sem-eventos">
          Nenhum evento. Registre a posição inicial (na data do saldo inicial da conta) ou a primeira aplicação.
        </p>
      )}
      <h2 className="conc-titulo">
        Realizado por mês · rendimento realizado no resgate
        <Ajuda titulo="Realizado">Aplicações e resgates pelo valor da conta corrente. Rendimento bruto, IR, IOF e tarifa vêm só das composições informadas (o rendimento acumulado do demonstrativo é informativo e não entra). Resgate “a informar” conta só pelo líquido.</Ajuda>
      </h2>
      {f.porMes.length ? (
        <RolagemIndicada className="rolagem quadro-tabela">
          <table className="tabela tabela-compacta" data-teste="tabela-realizado">
            <thead>
              <tr>
                <th>Mês</th>
                <th className="numero">Aplicado</th>
                <th className="numero">Resgatado (líquido)</th>
                <th className="numero">Principal resgatado</th>
                <th className="numero">Rendimento bruto</th>
                <th className="numero">IR · IOF · tarifa</th>
              </tr>
            </thead>
            <tbody>
              {f.porMes.map((m) => (
                <tr key={m.mes} data-teste={`realizado-${m.mes}`}>
                  <td className="conc-nowrap">
                    {mesExtenso(m.mes)}
                    {f.mesesFechados.includes(m.mes) && <div className="texto-secundario conc-mini">fechado</div>}
                  </td>
                  <td className="numero">{rs(m.aplicado)}</td>
                  <td className="numero">{rs(m.resgatadoLiquido)}</td>
                  <td className="numero">{rs(m.principalResgatado)}</td>
                  <td className="numero">{rs(m.rendimentoBruto)}</td>
                  <td className="numero conc-mini">
                    <div>IR {rs(m.ir)}</div>
                    <div>IOF {rs(m.iof)}</div>
                    <div>tarifa {rs(m.tarifa)}</div>
                    {m.resgatesAInformar > 0 && <div className="texto-secundario conc-mini">+ {plural(m.resgatesAInformar, 'resgate a informar', 'resgates a informar')}</div>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </RolagemIndicada>
      ) : (
        <p className="texto-secundario">Sem aplicações ou resgates registrados.</p>
      )}
      {registrar && <RegistrarEvento ficha={f} tipo={registrar} aoFechar={() => setRegistrar(null)} aoConcluir={recarregar} />}
      {comp && <InformarComposicao evento={comp} mesFechado={fechado(comp.data)} aoFechar={() => setComp(null)} aoConcluir={recarregar} />}
      {anotar && <Anotar evento={anotar} podeAceitar={Boolean(divergente && f.ultimaInformada!.evento === anotar.numero)} aoFechar={() => setAnotar(null)} aoConcluir={recarregar} />}
      {estornar && <EstornarEvento evento={estornar} mesFechado={fechado(estornar.data)} aoFechar={() => setEstornar(null)} aoConcluir={recarregar} />}
    </>
  );
}

async function baixarAnexo(id: string) {
  const r = await api.get<{ nome: string; tipo: string; conteudo_base64: string }>(`/api/aplicacoes/anexos/${id}/arquivo`);
  const bytes = Uint8Array.from(atob(r.conteudo_base64), (c) => c.charCodeAt(0));
  const url = URL.createObjectURL(new Blob([bytes as BlobPart], { type: r.tipo || 'application/octet-stream' }));
  const el = document.createElement('a');
  el.href = url;
  el.download = r.nome;
  el.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/* ---------------------------------------------------------------- composição (5 campos) */
interface CamposComposicao { principal: string; rendimento_bruto: string; ir: string; iof: string; tarifa: string }
const VAZIA: CamposComposicao = { principal: '', rendimento_bruto: '', ir: '0,00', iof: '0,00', tarifa: '0,00' };
const somaComposicao = (c: CamposComposicao) => centavos(c.principal) + centavos(c.rendimento_bruto) - centavos(c.ir) - centavos(c.iof) - centavos(c.tarifa);
function CamposDaComposicao({ valor, liquido, aoMudar }: { valor: CamposComposicao; liquido: number; aoMudar: (c: CamposComposicao) => void }) {
  const soma = somaComposicao(valor);
  const dif = liquido - soma;
  const campo = (k: keyof CamposComposicao, rotulo: string) => (
    <Campo rotulo={rotulo}>{(id) => <input id={id} inputMode="decimal" value={valor[k]} onChange={(e) => aoMudar({ ...valor, [k]: e.target.value })} placeholder="0,00" data-teste={`comp-${k}`} />}</Campo>
  );
  return (
    <>
      <div className="grade-form tres">
        {campo('principal', 'Principal resgatado *')}
        {campo('rendimento_bruto', 'Rendimento bruto *')}
        {campo('ir', 'IR retido')}
        {campo('iof', 'IOF')}
        {campo('tarifa', 'Tarifa')}
      </div>
      <p className="faixa-resumo-modal" data-teste="soma-composicao">
        <span>
          Líquido creditado <strong>{rs(liquido / 100)}</strong>
        </span>
        <span>
          Principal + rendimento − IR − IOF − tarifa <strong>{rs(soma / 100)}</strong>
        </span>
        <span className={dif === 0 ? 'conc-ok' : 'conc-dif'} data-teste="diferenca-composicao">
          Diferença {rs(Math.abs(dif) / 100)} {dif === 0 ? '✓' : ''}
        </span>
      </p>
    </>
  );
}

function InformarComposicao({ evento, mesFechado, aoFechar, aoConcluir }: { evento: Evento; mesFechado: boolean; aoFechar: () => void; aoConcluir: () => void }) {
  const atual = evento.composicao;
  const [c, setC] = useState<CamposComposicao>(atual ? { principal: rs(atual.principal).replace(/[^\d,.]/g, ''), rendimento_bruto: rs(atual.rendimentoBruto).replace(/[^\d,.]/g, ''), ir: rs(atual.ir).replace(/[^\d,.]/g, ''), iof: rs(atual.iof).replace(/[^\d,.]/g, ''), tarifa: rs(atual.tarifa).replace(/[^\d,.]/g, '') } : VAZIA);
  const [origem, setOrigem] = useState('comprovante');
  const [documento, setDocumento] = useState('');
  const [just, setJust] = useState('');
  const [previa, setPrevia] = useState<Efeito | null>(null);
  const [erro, setErro] = useState('');
  const [enviando, setEnviando] = useState(false);
  const liquido = centavos(evento.valor);
  const fecha = somaComposicao(c) === liquido && c.principal !== '' && c.rendimento_bruto !== '';
  const ok = fecha && documento.trim().length >= 3 && (!atual || just.trim().length >= 10) && !mesFechado;
  useEffect(() => setPrevia(null), [c, origem, documento, just]);
  const enviar = async (comPrevia: boolean) => {
    setEnviando(true);
    setErro('');
    try {
      const r = await api.post<{ efeito: Efeito }>(`/api/aplicacoes/eventos/${evento.numero}/composicao`, { ...c, origem, documento, justificativa: atual ? just : undefined, previa: comPrevia });
      if (comPrevia) setPrevia(r.efeito);
      else aoConcluir();
    } catch (e) {
      setErro(msg(e));
    } finally {
      setEnviando(false);
    }
  };
  return (
    <Modal
      titulo={`${atual ? 'Corrigir' : 'Informar'} composição — ${evento.numero}`}
      aoFechar={aoFechar}
      largo
      rodape={
        <>
          <span className="texto-secundario conc-rodape-efeito">{previa ? 'Confira a prévia e confirme.' : 'Nada é gravado até confirmar.'}</span>
          <button type="button" className="botao-secundario" onClick={aoFechar}>
            Voltar
          </button>
          {previa ? (
            <button type="button" className="botao-primario" disabled={!ok || enviando} onClick={() => void enviar(false)} data-teste="confirmar-composicao">
              Confirmar composição
            </button>
          ) : (
            <button type="button" className="botao-primario" disabled={!ok || enviando} onClick={() => void enviar(true)} data-teste="previa-composicao">
              Ver prévia
            </button>
          )}
        </>
      }
    >
      <div data-teste="modal-composicao">
        <p className="faixa-resumo-modal">
          <span>
            Resgate de {dataBr(evento.data)} · líquido creditado <strong>{rs(evento.valor)}</strong>
            {evento.encerra ? ' · resgate total' : ''}
          </span>
        </p>
        {mesFechado ? (
          <p className="aviso aviso-erro" role="alert" data-teste="composicao-mes-fechado">
            O mês deste resgate está fechado: incluir ou alterar a composição exige reabrir o período e fechar de novo (o fechamento anterior fica preservado). Sem reabrir, registre só anexo ou observação.
          </p>
        ) : (
          <>
            <CamposDaComposicao valor={c} liquido={liquido} aoMudar={setC} />
            <div className="grade-form">
              <Campo rotulo="Origem">
                {(id) => (
                  <select id={id} value={origem} onChange={(e) => setOrigem(e.target.value)} data-teste="origem-composicao">
                    {ORIGENS.filter(([k]) => k !== 'extrato').map(([k, v]) => (
                      <option key={k} value={k}>
                        {v}
                      </option>
                    ))}
                  </select>
                )}
              </Campo>
              <Campo rotulo="Documento ou referência *">{(id) => <input id={id} maxLength={300} value={documento} onChange={(e) => setDocumento(e.target.value)} placeholder="Ex.: comprovante de resgate 05/10" data-teste="documento-composicao" />}</Campo>
            </div>
            {atual && (
              <Campo rotulo="Justificativa da correção *" dica="A versão anterior fica no histórico.">
                {(id) => <textarea id={id} rows={2} maxLength={500} value={just} onChange={(e) => setJust(e.target.value)} placeholder="Ao menos 10 caracteres" data-teste="justificativa-composicao" />}
              </Campo>
            )}
            <p className="texto-secundario">Os 5 campos são informados separadamente — nada é deduzido do líquido, nem se o resgate foi parcial ou total.</p>
          </>
        )}
        {previa && <EfeitoPrevia efeito={previa} teste="previa-efeito-composicao" />}
        <Erro texto={erro} teste="erro-composicao" />
      </div>
    </Modal>
  );
}

/* ---------------------------------------------------------------- registrar evento */
function RegistrarEvento({ ficha, tipo, aoFechar, aoConcluir }: { ficha: Ficha; tipo: TipoEvento; aoFechar: () => void; aoConcluir: () => void }) {
  const [data, setData] = useState('');
  const [valor, setValor] = useState('');
  const [bruta, setBruta] = useState('');
  const [original, setOriginal] = useState('');
  const [encerra, setEncerra] = useState(false);
  const [origem, setOrigem] = useState(tipo === 'pos' || tipo === 'pia' ? 'demonstrativo' : 'comprovante');
  const [documento, setDocumento] = useState('');
  const [observacao, setObservacao] = useState('');
  const [comComposicao, setComComposicao] = useState(false);
  const [c, setC] = useState<CamposComposicao>(VAZIA);
  const [docComp, setDocComp] = useState('');
  const [previa, setPrevia] = useState<Efeito | null>(null);
  const [erro, setErro] = useState('');
  const [enviando, setEnviando] = useState(false);
  useEffect(() => setPrevia(null), [data, valor, bruta, original, encerra, origem, documento, observacao, comComposicao, c, docComp]);
  const liquido = centavos(valor);
  const compOk = !comComposicao || (somaComposicao(c) === liquido && c.principal !== '' && c.rendimento_bruto !== '' && docComp.trim().length >= 3);
  const ok = Boolean(data) && liquido > 0 && documento.trim().length >= 3 && compOk;
  const corpo = (comPrevia: boolean) => ({
    tipo,
    data,
    valor,
    posicao_bruta: (tipo === 'pia' || tipo === 'pos') && bruta ? bruta : undefined,
    data_aplicacao_original: tipo === 'pia' && original ? original : undefined,
    encerra: tipo === 'res' ? encerra : undefined,
    origem,
    documento,
    observacao: observacao || undefined,
    composicao: tipo === 'res' && comComposicao ? { ...c, origem: 'comprovante', documento: docComp } : undefined,
    previa: comPrevia,
  });
  const enviar = async (comPrevia: boolean) => {
    setEnviando(true);
    setErro('');
    try {
      const r = await api.post<{ efeito: Efeito }>(`/api/aplicacoes/${ficha.numero}/eventos`, corpo(comPrevia));
      if (comPrevia) setPrevia(r.efeito);
      else aoConcluir();
    } catch (e) {
      setErro(msg(e));
    } finally {
      setEnviando(false);
    }
  };
  const titulo = { pia: 'Registrar posição inicial', apl: 'Registrar aplicação', res: 'Registrar resgate', pos: 'Registrar posição informada (demonstrativo)' }[tipo];
  return (
    <Modal
      titulo={`${titulo} — ${ficha.numero}`}
      aoFechar={aoFechar}
      largo={tipo === 'res'}
      medio={tipo !== 'res'}
      rodape={
        <>
          <span className="texto-secundario conc-rodape-efeito">{previa ? 'Confira a prévia e confirme.' : 'Nada é gravado até confirmar.'}</span>
          <button type="button" className="botao-secundario" onClick={aoFechar}>
            Voltar
          </button>
          {previa ? (
            <button type="button" className="botao-primario" disabled={!ok || enviando} onClick={() => void enviar(false)} data-teste="confirmar-evento">
              Confirmar
            </button>
          ) : (
            <button type="button" className="botao-primario" disabled={!ok || enviando} onClick={() => void enviar(true)} data-teste="previa-evento">
              Ver prévia
            </button>
          )}
        </>
      }
    >
      <div data-teste={`modal-evento-${tipo}`}>
        {tipo === 'pia' && <p className="texto-secundario">A posição inicial é o principal que já estava aplicado na data do saldo inicial da conta (ou antes). Não cria saída bancária: o dinheiro já não estava no saldo da conta. O que foi aplicado depois entra como aplicação, conciliada com o extrato.</p>}
        {tipo === 'apl' && <p className="texto-secundario">Pode ser registrada antes do extrato: fica “aguardando extrato” e é conciliada depois com o movimento da conta, contando o dinheiro uma vez.</p>}
        {tipo === 'res' && <p className="texto-secundario">Registre o valor líquido creditado na conta. Sem a composição, a posição fica provisória até ela chegar; nada é deduzido do líquido.</p>}
        {tipo === 'pos' && <p className="texto-secundario">Principal do demonstrativo do banco na data. Diferença do calculado vira pendência, sem ajuste automático.</p>}
        <div className="grade-form tres">
          <Campo rotulo={tipo === 'pia' ? 'Data de referência *' : tipo === 'pos' ? 'Data do demonstrativo *' : 'Data *'}>{(id) => <input id={id} type="date" value={data} onChange={(e) => setData(e.target.value)} data-teste="data-evento" />}</Campo>
          <Campo rotulo={tipo === 'res' ? 'Valor líquido creditado *' : tipo === 'apl' ? 'Valor aplicado *' : 'Principal *'}>{(id) => <input id={id} inputMode="decimal" value={valor} onChange={(e) => setValor(e.target.value)} placeholder="0,00" data-teste="valor-evento" />}</Campo>
          {(tipo === 'pia' || tipo === 'pos') && <Campo rotulo="Posição bruta (informativa)">{(id) => <input id={id} inputMode="decimal" value={bruta} onChange={(e) => setBruta(e.target.value)} placeholder="opcional" data-teste="bruta-evento" />}</Campo>}
          {tipo === 'pia' && <Campo rotulo="Aplicada originalmente em">{(id) => <input id={id} type="date" value={original} onChange={(e) => setOriginal(e.target.value)} data-teste="original-evento" />}</Campo>}
          <Campo rotulo="Origem">
            {(id) => (
              <select id={id} value={origem} onChange={(e) => setOrigem(e.target.value)} data-teste="origem-evento">
                {ORIGENS.map(([k, v]) => (
                  <option key={k} value={k}>
                    {v}
                  </option>
                ))}
              </select>
            )}
          </Campo>
          <Campo rotulo="Documento ou referência *">{(id) => <input id={id} maxLength={300} value={documento} onChange={(e) => setDocumento(e.target.value)} data-teste="documento-evento" />}</Campo>
          <Campo rotulo="Observação">{(id) => <input id={id} maxLength={500} value={observacao} onChange={(e) => setObservacao(e.target.value)} data-teste="observacao-evento" />}</Campo>
        </div>
        {tipo === 'res' && (
          <>
            <label className="conc-check">
              <input type="checkbox" checked={encerra} onChange={(e) => setEncerra(e.target.checked)} data-teste="encerra-evento" /> Resgate total: encerra a aplicação (declarado por você; não é deduzido do valor)
            </label>
            <div className="chips-estados" role="group" aria-label="Composição">
              <button type="button" className={`chip${!comComposicao ? ' ativo' : ''}`} aria-pressed={!comComposicao} onClick={() => setComComposicao(false)} data-teste="composicao-a-informar">
                Composição a informar
              </button>
              <button type="button" className={`chip${comComposicao ? ' ativo' : ''}`} aria-pressed={comComposicao} onClick={() => setComComposicao(true)} data-teste="composicao-agora">
                Informar composição agora
              </button>
            </div>
            {comComposicao && (
              <>
                <CamposDaComposicao valor={c} liquido={liquido} aoMudar={setC} />
                <Campo rotulo="Documento da composição *">{(id) => <input id={id} maxLength={300} value={docComp} onChange={(e) => setDocComp(e.target.value)} data-teste="documento-comp-evento" />}</Campo>
              </>
            )}
          </>
        )}
        {previa && <EfeitoPrevia efeito={previa} teste="previa-efeito-evento" />}
        <Erro texto={erro} teste="erro-evento" />
      </div>
    </Modal>
  );
}

/* ---------------------------------------------------------------- anotar · estornar */
function Anotar({ evento, podeAceitar, aoFechar, aoConcluir }: { evento: Evento; podeAceitar: boolean; aoFechar: () => void; aoConcluir: () => void }) {
  const [tipo, setTipo] = useState<'observacao' | 'anexo' | 'aceite_divergencia'>('observacao');
  const [texto, setTexto] = useState('');
  const [arquivo, setArquivo] = useState<{ nome: string; conteudo_base64: string } | null>(null);
  const [erro, setErro] = useState('');
  const [enviando, setEnviando] = useState(false);
  const minimo = tipo === 'aceite_divergencia' ? 10 : 4;
  const ok = texto.trim().length >= minimo && (tipo !== 'anexo' || arquivo);
  const enviar = async () => {
    setEnviando(true);
    setErro('');
    try {
      await api.post(`/api/aplicacoes/eventos/${evento.numero}/anotacoes`, { tipo, texto, arquivo: tipo === 'anexo' ? arquivo : undefined });
      aoConcluir();
    } catch (e) {
      setErro(msg(e));
    } finally {
      setEnviando(false);
    }
  };
  return (
    <Modal
      titulo={`Anexo ou observação — ${evento.numero}`}
      aoFechar={aoFechar}
      medio
      rodape={
        <>
          <button type="button" className="botao-secundario" onClick={aoFechar}>
            Voltar
          </button>
          <button type="button" className="botao-primario" disabled={!ok || enviando} onClick={() => void enviar()} data-teste="confirmar-anotacao">
            Registrar
          </button>
        </>
      }
    >
      <div data-teste="modal-anotacao">
        <p className="texto-secundario">Não muda valores. Permitido também com o mês fechado.</p>
        <div className="chips-estados" role="group" aria-label="Tipo">
          {([['observacao', 'Observação'], ['anexo', 'Anexo'], ...(podeAceitar ? [['aceite_divergencia', 'Registrar ciência da divergência']] : [])] as [typeof tipo, string][]).map(([k, v]) => (
            <button key={k} type="button" className={`chip${tipo === k ? ' ativo' : ''}`} aria-pressed={tipo === k} onClick={() => setTipo(k)} data-teste={`anotacao-${k}`}>
              {v}
            </button>
          ))}
        </div>
        {tipo === 'anexo' && <Campo rotulo="Arquivo *">{(id) => <input id={id} type="file" accept=".pdf,.png,.jpg,.jpeg" onChange={async (e) => setArquivo((await arquivoParaApi(e.target.files?.[0] ?? null)) ?? null)} data-teste="arquivo-anotacao" />}</Campo>}
        <Campo rotulo={tipo === 'aceite_divergencia' ? 'Justificativa da ciência *' : 'Texto *'}>
          {(id) => <textarea id={id} rows={2} maxLength={500} value={texto} onChange={(e) => setTexto(e.target.value)} placeholder={`Ao menos ${minimo} caracteres`} data-teste="texto-anotacao" />}
        </Campo>
        {tipo === 'aceite_divergencia' && <p className="texto-secundario">A ciência fica registrada com a justificativa, mas não resolve a divergência: a pendência e a diferença continuam visíveis até um demonstrativo que confira ou a correção dos eventos.</p>}
        <Erro texto={erro} teste="erro-anotacao" />
      </div>
    </Modal>
  );
}

function EstornarEvento({ evento, mesFechado, aoFechar, aoConcluir }: { evento: Evento; mesFechado: boolean; aoFechar: () => void; aoConcluir: () => void }) {
  const bloqueio = evento.conciliacao ? `${evento.numero} está conciliado com o extrato (${evento.conciliacao}): desfaça a conciliação na Conciliação bancária antes de estornar.` : mesFechado ? 'O mês deste evento está fechado: estornar exige reabrir o período.' : '';
  const previa = useCarga(() => (bloqueio ? Promise.resolve(null) : api.post<{ efeito: Efeito }>(`/api/aplicacoes/eventos/${evento.numero}/estorno`, { previa: true })), [evento.numero, bloqueio]);
  const [just, setJust] = useState('');
  const [erro, setErro] = useState('');
  const [enviando, setEnviando] = useState(false);
  const enviar = async () => {
    setEnviando(true);
    setErro('');
    try {
      await api.post(`/api/aplicacoes/eventos/${evento.numero}/estorno`, { justificativa: just });
      aoConcluir();
    } catch (e) {
      setErro(msg(e));
    } finally {
      setEnviando(false);
    }
  };
  return (
    <Modal
      titulo={`Estornar ${TIPO[evento.tipo].rotulo.toLowerCase()} — ${evento.numero}`}
      aoFechar={aoFechar}
      medio
      rodape={
        <>
          <button type="button" className="botao-secundario" onClick={aoFechar}>
            Voltar
          </button>
          <button type="button" className="botao-primario" disabled={Boolean(bloqueio) || just.trim().length < 10 || enviando} onClick={() => void enviar()} data-teste="confirmar-estorno">
            Estornar
          </button>
        </>
      }
    >
      <div data-teste="modal-estorno">
        <p className="faixa-resumo-modal">
          <span>
            {TIPO[evento.tipo].rotulo} de {dataBr(evento.data)} · {rs(evento.valor)}
          </span>
        </p>
        {bloqueio ? (
          <p className="aviso aviso-erro" role="alert" data-teste="estorno-bloqueado">
            {bloqueio}
          </p>
        ) : (
          <>
            {previa.dados ? <EfeitoPrevia efeito={previa.dados.efeito} teste="previa-efeito-estorno" /> : <EstadoCarga carregando={previa.carregando} erro={previa.erro} recarregar={previa.recarregar} teste="previa-estorno" />}
            <p className="texto-secundario">O evento continua visível como estornado; nada é apagado.</p>
            <Campo rotulo="Justificativa *">{(id) => <textarea id={id} rows={2} maxLength={500} value={just} onChange={(e) => setJust(e.target.value)} placeholder="Ao menos 10 caracteres" data-teste="justificativa-estorno" />}</Campo>
          </>
        )}
        <Erro texto={erro} teste="erro-estorno" />
      </div>
    </Modal>
  );
}

/* ---------------------------------------------------------------- conciliação com o extrato (aberta na Conciliação) */
interface CandidatosAplicacao {
  conta: string;
  movimentos: { numero: string; data: string; valor: string; descricao: string }[];
  total: string;
  janelaDias: number;
  eventos: { numero: string; tipo: 'apl' | 'res'; data: string; valor: string; valorConta: string; aplicacao: string; descricao: string; sugerido: boolean; ambiguo: number | null }[];
}
export function ConciliarAplicacao({ movimentos, aoFechar, aoConcluir }: { movimentos: string[]; aoFechar: () => void; aoConcluir: () => void }) {
  const { pode } = useSessao();
  const carga = useCarga(() => api.get<CandidatosAplicacao>(`/api/aplicacoes/conciliacao/candidatos?movimentos=${encodeURIComponent(movimentos.join(','))}`), [movimentos.join(',')]);
  const podeRegistrar = pode('registrar', 'aplicacao') && movimentos.length === 1;
  const lista = useCarga(() => (podeRegistrar ? api.get<Lista>('/api/aplicacoes') : Promise.resolve(null)), [podeRegistrar]);
  const [marcados, setMarcados] = useState<string[]>([]);
  const [modo, setModo] = useState<'vincular' | 'registrar'>('vincular');
  const [apc, setApc] = useState('');
  const [documento, setDocumento] = useState('');
  const [encerra, setEncerra] = useState(false);
  const [previa, setPrevia] = useState(false);
  const [erro, setErro] = useState('');
  const [enviando, setEnviando] = useState(false);
  const c = carga.dados;
  const total = c ? centavos(c.total) : 0;
  const soma = c ? c.eventos.filter((e) => marcados.includes(e.numero)).reduce((s, e) => s + centavos(e.valorConta), 0) : 0;
  const dif = total - soma;
  const contaMov = useMemo(() => (lista.dados && c ? lista.dados.aplicacoes.filter((a) => !a.encerrada && a.conta.id === c.conta) : []), [lista.dados, c]);
  useEffect(() => setPrevia(false), [marcados, modo, apc, documento, encerra]);
  const ehResgate = total > 0;
  const okVincular = marcados.length > 0 && dif === 0;
  const okRegistrar = Boolean(apc) && documento.trim().length >= 3;
  const enviar = async (comPrevia: boolean) => {
    setEnviando(true);
    setErro('');
    try {
      const sugeridos = c!.eventos.filter((e) => marcados.includes(e.numero)).every((e) => e.sugerido);
      await api.post('/api/aplicacoes/conciliacao', modo === 'registrar' ? { movimentos, registrar: { aplicacao: apc, documento, encerra: ehResgate ? encerra : undefined }, previa: comPrevia } : { movimentos, eventos: marcados, origem: sugeridos ? 'sugestao' : 'manual', previa: comPrevia });
      if (comPrevia) setPrevia(true);
      else aoConcluir();
    } catch (e) {
      setErro(msg(e));
    } finally {
      setEnviando(false);
    }
  };
  const ok = modo === 'vincular' ? okVincular : okRegistrar;
  return (
    <Modal
      titulo="Conciliar aplicação ou resgate"
      aoFechar={aoFechar}
      largo
      rodape={
        <>
          <span className="texto-secundario conc-rodape-efeito">{previa ? 'Prévia conferida: nada foi gravado. Confirme.' : 'Nada é gravado até confirmar.'}</span>
          <button type="button" className="botao-secundario" onClick={aoFechar}>
            Voltar
          </button>
          {previa ? (
            <button type="button" className="botao-primario" disabled={!ok || enviando} onClick={() => void enviar(false)} data-teste="confirmar-conciliar-aplicacao">
              {modo === 'registrar' ? 'Registrar e conciliar' : 'Conciliar'}
            </button>
          ) : (
            <button type="button" className="botao-primario" disabled={!c || !ok || enviando} onClick={() => void enviar(true)} data-teste="previa-conciliar-aplicacao">
              Conferir (prévia)
            </button>
          )}
        </>
      }
    >
      {!c ? (
        <EstadoCarga carregando={carga.carregando} erro={carga.erro} recarregar={carga.recarregar} teste="candidatos-aplicacao" />
      ) : (
        <div data-teste="modal-conciliar-aplicacao">
          <div className="faixa-resumo-modal conc-bloco">
            {c.movimentos.map((m) => (
              <div key={m.numero}>
                <strong>{m.numero}</strong> · {dataBr(m.data)} · {m.descricao} · {valorConta(m.valor)}
              </div>
            ))}
          </div>
          {podeRegistrar && (
            <div className="chips-estados" role="group" aria-label="Como conciliar">
              <button type="button" className={`chip${modo === 'vincular' ? ' ativo' : ''}`} aria-pressed={modo === 'vincular'} onClick={() => setModo('vincular')} data-teste="modo-vincular">
                Com evento já registrado
              </button>
              <button type="button" className={`chip${modo === 'registrar' ? ' ativo' : ''}`} aria-pressed={modo === 'registrar'} onClick={() => setModo('registrar')} data-teste="modo-registrar">
                Registrar {ehResgate ? 'o resgate' : 'a aplicação'} a partir do movimento
              </button>
            </div>
          )}
          {modo === 'vincular' ? (
            <>
              <h3 className="conc-titulo">
                Aplicações e resgates aguardando extrato (mesma conta)
                <Ajuda titulo="Eventos da aplicação">{`APL e RES desta conta ainda não conciliados. "Sugerido": mesmo valor na conta e data até ${c.janelaDias} dias — vem DESMARCADO; marque e confirme. Valores iguais ficam "ambíguos": a escolha é sua.`}</Ajuda>
              </h3>
              {c.eventos.length ? (
                <RolagemIndicada className="rolagem quadro-tabela">
                  <table className="tabela tabela-compacta" data-teste="eventos-conciliar">
                    <thead>
                      <tr>
                        <th className="col-selecao" aria-label="Marcar" />
                        <th>Evento</th>
                        <th>Data</th>
                        <th>Aplicação</th>
                        <th className="numero">Na conta</th>
                      </tr>
                    </thead>
                    <tbody>
                      {c.eventos.map((e) => (
                        <tr key={e.numero} data-teste={`candidato-${e.numero}`}>
                          <td className="col-selecao">
                            <input type="checkbox" aria-label={`Marcar ${e.numero}`} checked={marcados.includes(e.numero)} onChange={() => setMarcados((m) => (m.includes(e.numero) ? m.filter((x) => x !== e.numero) : [...m, e.numero]))} data-teste={`marcar-${e.numero}`} />
                          </td>
                          <td className="conc-nowrap">
                            <strong>{e.numero}</strong> {e.tipo === 'apl' ? 'aplicação' : 'resgate'}
                            {e.sugerido && (
                              <div>
                                <SeloSituacao texto="Sugerido · mesmo valor e conta" tom="positivo" />
                              </div>
                            )}
                            {e.ambiguo && (
                              <div data-teste="candidato-ambiguo">
                                <SeloSituacao texto={`Candidato · ambíguo (${e.ambiguo})`} tom="parcial" />
                              </div>
                            )}
                          </td>
                          <td className="conc-nowrap">{dataBr(e.data)}</td>
                          <td>
                            {e.descricao}
                            <div className="texto-secundario conc-mini">{e.aplicacao}</div>
                          </td>
                          <td className="numero conc-nowrap">{valorConta(e.valorConta)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </RolagemIndicada>
              ) : (
                <p className="texto-secundario" data-teste="sem-candidatos-aplicacao">
                  Nenhuma aplicação ou resgate aguardando extrato nesta conta.{podeRegistrar ? ' Registre a partir do movimento.' : ''}
                </p>
              )}
              <p className="faixa-resumo-modal" data-teste="totais-aplicacao">
                <span>
                  Movimentos <strong>{rs(total / 100)}</strong>
                </span>
                <span>
                  Eventos marcados <strong>{rs(soma / 100)}</strong>
                </span>
                <span className={dif === 0 && marcados.length ? 'conc-ok' : 'conc-dif'} data-teste="totais-aplicacao-diferenca">
                  Diferença {rs(Math.abs(dif) / 100)} {dif === 0 && marcados.length ? '✓' : ''}
                </span>
              </p>
            </>
          ) : (
            <>
              <p className="texto-secundario">
                {ehResgate ? 'O resgate' : 'A aplicação'} nasce com a data e o valor deste movimento e já fica conciliad{ehResgate ? 'o' : 'a'} com ele.
                {ehResgate ? ' A composição fica “a informar” (posição provisória) até você informá-la na ficha da aplicação.' : ''}
              </p>
              <div className="grade-form">
                <Campo rotulo="Aplicação *">
                  {(id) => (
                    <select id={id} value={apc} onChange={(e) => setApc(e.target.value)} data-teste="aplicacao-registrar">
                      <option value="">Escolha…</option>
                      {contaMov.map((a) => (
                        <option key={a.numero} value={a.numero}>
                          {a.numero} · {a.descricao} · {contaTexto(a.conta)}
                        </option>
                      ))}
                    </select>
                  )}
                </Campo>
                <Campo rotulo="Documento ou referência *">{(id) => <input id={id} maxLength={300} value={documento} onChange={(e) => setDocumento(e.target.value)} placeholder="Ex.: extrato de 06/10" data-teste="documento-registrar-aplicacao" />}</Campo>
              </div>
              {ehResgate && (
                <label className="conc-check">
                  <input type="checkbox" checked={encerra} onChange={(e) => setEncerra(e.target.checked)} data-teste="encerra-registrar" /> Resgate total: encerra a aplicação
                </label>
              )}
            </>
          )}
          {previa && (
            <p className="faixa-resumo-modal" data-teste="previa-conciliar-aplicacao-ok">
              <span>Prévia conferida: as somas fecham e nada foi gravado.</span>
            </p>
          )}
          <Erro texto={erro} teste="erro-conciliar-aplicacao" />
        </div>
      )}
    </Modal>
  );
}
